import type { FC } from 'react';

import { Placeholder, useCms, useContent, useImageMapper, useRendering, useSetting } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { CircledNumber } from '@eneco/components/ui/CircledNumber/CircledNumber';
import {
  Badge,
  Box,
  ButtonLink,
  Card,
  Grid,
  Heading,
  IconLoader,
  Image,
  Ribbon as SparkyRibbon,
  Stack,
  Stretch,
  Text,
  TextLink,
} from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import type { BoxProps, HeadingProps } from '@eneco/sparky/types';

import type { ContentCardContent } from './content-card-content.types';
import { CONTENT_CARD_SLOTS, resolveSlot } from './slots';

/**
 * `ContentCard` block for the CMS-agnostic lane, serving **both** providers.
 *
 * `buttonLink`/`link` carry their own display text on stored content, even though the generated schema
 * does not declare it (the dataset is schemaless). `jss-promo-box` has no Sanity-side field on
 * `ContentCard` itself (unlike `SplitView`, which does model it), so that slot always renders empty
 * there.
 *
 * Known simplification: `showRibbonPlaceholder`/`variant` are not fields on `ContentCard` — the legacy
 * component receives them as JSX props from a *parent* component (e.g. a carousel rendering several
 * cards with a shared invisible-ribbon spacer). The CMS-agnostic registry gives every component nothing
 * but its own content (`ComponentsDefinition`'s contract, `types.ts`) — there is no channel for a parent
 * to pass configuration down — so both always take their default (`showRibbonPlaceholder=false`,
 * `variant='default'`).
 *
 * Drops the Sitecore Experience Editor `wrap()` calls — this lane never runs inside the Experience
 * Editor. Also drops the legacy component's visually-hidden "step N" announcement on `cardNumber`
 * (`useI18nTranslation`/`useFormatter` from `@eneco/i18n`) — the `lib:cms-components` module boundary
 * (`nx.json`) does not permit depending on `@eneco/i18n`; the numbered badge itself (`CircledNumber`)
 * still renders.
 */
const ContentCard: FC = () => {
  const { adapter } = useCms();
  const content = useContent<ContentCardContent>();
  const rendering = useRendering();
  const mapImage = useImageMapper();
  const isDesktop = useMediaQuery('md');

  const isElevated = useSetting('setElevation') === 'true';
  const isCompactOnMobile = useSetting('mobileVariant') === 'compact';
  const isCompactOnDesktop = useSetting('desktopVariant') === 'compact';
  const isCompactCard = (isDesktop && isCompactOnDesktop) || (!isDesktop && isCompactOnMobile);
  const backgroundColor = useSetting('backgroundColor') as BoxProps['backgroundColor'];
  const hasBackgroundColor = Boolean(backgroundColor);
  const headingLevel = (useSetting('headingLevel') || 'h3') as HeadingProps['as'];
  const objectFit = useSetting('objectFit') === 'contain' ? 'contain' : 'cover';

  const imageProps = mapImage(content.image);
  const hasImage = Boolean(imageProps?.src);
  const richText = adapter.toRichText(content.content);

  const promoSlot = resolveSlot(rendering, CONTENT_CARD_SLOTS.promo);
  // Two distinct fields, not a fallback pair: `buttonLink` is the primary CTA (a `ButtonLink`),
  // `link` a secondary one (a `TextLink`) — both can render at once, as siblings.
  const buttonCta = adapter.toLink(content.buttonLink);
  const textCta = adapter.toLink(content.link);
  const mainSlot = resolveSlot(rendering, CONTENT_CARD_SLOTS.main);

  const cardBody = (
    <Stretch>
      <Stack
        direction={isCompactCard ? 'row' : 'column'}
        gap={{ initial: isCompactCard ? '3' : '0', md: isCompactCard ? '6' : '0' }}>
        {hasImage && (
          <Stack.Item shrink={false}>
            <Grid gridTemplateColumns="40px auto 1fr" gridTemplateRows="20px auto 1fr">
              <Grid.Item gridColumn="1 / -1" gridRow="1 / -1">
                <Image
                  alt={imageProps?.alt ?? ''}
                  width={{ initial: isCompactOnMobile ? '80px' : '100%', md: isCompactOnDesktop ? '160px' : '100%' }}
                  height={{ initial: isCompactOnMobile ? '80px' : 'auto', md: isCompactOnDesktop ? '160px' : 'auto' }}
                  src={imageProps?.src ?? ''}
                  sources={imageProps?.sources}
                  objectFit={objectFit}
                  aspectRatio="16/9"
                />
              </Grid.Item>
              {content.cardNumber !== undefined && !isCompactCard && (
                <Grid.Item aria-hidden="true" gridColumn="2 / 2" gridRow="2 / 2">
                  <CircledNumber>{content.cardNumber}</CircledNumber>
                </Grid.Item>
              )}
              <Grid.Item gridColumn="1 / -1" gridRow="1 / -1">
                {promoSlot ? <Placeholder name={promoSlot} /> : null}
              </Grid.Item>
            </Grid>
          </Stack.Item>
        )}
        {(content.title || content.badgeText || richText) && (
          <Stretch>
            <Box
              paddingX={isElevated && !isCompactCard ? '6' : '0'}
              paddingTop={(isElevated || hasImage) && !isCompactCard ? '6' : '0'}
              paddingBottom={isElevated && !isCompactCard ? '6' : '0'}>
              <Stretch>
                <Stack gap={isCompactCard ? '2' : '6'}>
                  {(content.title || content.badgeText) && (
                    <Stack.Item>
                      <Stack gap={isCompactCard ? '1' : '2'}>
                        {content.title && (
                          <Heading as={headingLevel} size={{ initial: 'XS', md: 'S' }}>
                            {content.title}
                          </Heading>
                        )}
                        {content.badgeText && (
                          <Stack.Item>
                            <Badge size="M">{content.badgeText}</Badge>
                          </Stack.Item>
                        )}
                      </Stack>
                    </Stack.Item>
                  )}
                  <Stack.Item grow>
                    <Stack gap={isCompactCard ? '2' : '6'}>
                      {richText && (
                        <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
                          <RichText html={richText} />
                        </Text>
                      )}
                      {mainSlot ? <Placeholder name={mainSlot} /> : null}
                    </Stack>
                  </Stack.Item>
                  {buttonCta?.text && (
                    <Stack.Item>
                      <Stretch width={{ initial: true, md: false }}>
                        <ButtonLink href={buttonCta.href} target={buttonCta.target}>
                          {buttonCta.text}
                        </ButtonLink>
                      </Stretch>
                    </Stack.Item>
                  )}
                  {textCta?.text && (
                    <Stack.Item>
                      <TextLink
                        href={textCta.href}
                        target={textCta.target}
                        {...(hasBackgroundColor
                          ? { color: 'textOnBackgroundVarOne', emphasis: 'low' }
                          : { emphasis: 'high' })}>
                        {textCta.text}
                      </TextLink>
                    </Stack.Item>
                  )}
                </Stack>
              </Stretch>
            </Box>
          </Stretch>
        )}
      </Stack>
    </Stretch>
  );

  if (!isElevated) {
    return <Stack>{cardBody}</Stack>;
  }

  return (
    <Stack>
      <Stretch>
        <Card overflow="hidden">
          {content.ribbonText && <ContentCardRibbon icon={content.ribbonIcon} text={content.ribbonText} />}
          <Stretch>
            <Box backgroundColor={hasBackgroundColor ? backgroundColor : 'backgroundPrimary'}>
              {isCompactCard ? <Box padding="6">{cardBody}</Box> : cardBody}
            </Box>
          </Stretch>
        </Card>
      </Stretch>
    </Stack>
  );
};

const ContentCardRibbon: FC<{ icon?: string; text: string }> = ({ icon, text }) => (
  <SparkyRibbon>
    <Stack direction="row" alignY="center" gap="2" alignX="center">
      {icon && (
        <Stack.Item>
          <IconLoader iconName={icon} color="iconInverted" size="small" />
        </Stack.Item>
      )}
      <Stack.Item>
        <Text display="inline">{text}</Text>
      </Stack.Item>
    </Stack>
  </SparkyRibbon>
);

export default ContentCard;
